<template>
  <div class="settings-view">
    <div class="content-container">
      <div class="settings-header">
        <h1>⚙️ Einstellungen & Debug</h1>
      </div>

      <!-- Spotify Status -->
      <div class="settings-section">
        <h2>Spotify-Verbindung</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Status:</span>
            <SpotifyStatusBadge />
          </div>
          
          <div v-if="userProfile" class="info-item">
            <span class="info-label">Benutzer:</span>
            <span class="info-value">{{ userProfile.display_name }}</span>
          </div>
          
          <div v-if="userProfile" class="info-item">
            <span class="info-label">E-Mail:</span>
            <span class="info-value">{{ userProfile.email }}</span>
          </div>
          
          <div v-if="userProfile" class="info-item">
            <span class="info-label">Produkt:</span>
            <span class="info-value">{{ userProfile.product }}</span>
          </div>
          
          <div v-if="accessToken" class="info-item">
            <span class="info-label">Access Token:</span>
            <code class="token-display">{{ truncatedToken }}</code>
          </div>
        </div>
      </div>

      <!-- Web Playback SDK -->
      <div class="settings-section">
        <h2>Web Playback SDK (Premium)</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Player bereit:</span>
            <span :class="['status-badge', isReady ? 'status-success' : 'status-error']">
              {{ isReady ? '✓ Ja' : '✕ Nein' }}
            </span>
          </div>
          
          <div v-if="deviceId" class="info-item">
            <span class="info-label">Device ID:</span>
            <code class="token-display">{{ deviceId }}</code>
          </div>
        </div>

        <PrimaryButton
          v-if="isPremium && !isReady"
          @click="connectPlayer"
          size="sm"
        >
          Player verbinden
        </PrimaryButton>
      </div>

      <!-- Device Orientation -->
      <div class="settings-section">
        <h2>Geräte-Orientierung</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Unterstützt:</span>
            <span :class="['status-badge', isSupported ? 'status-success' : 'status-error']">
              {{ isSupported ? '✓ Ja' : '✕ Nein' }}
            </span>
          </div>
          
          <div class="info-item">
            <span class="info-label">Berechtigung:</span>
            <span :class="['status-badge', permissionGranted ? 'status-success' : 'status-error']">
              {{ permissionGranted ? '✓ Erteilt' : '✕ Nicht erteilt' }}
            </span>
          </div>
          
          <div class="info-item">
            <span class="info-label">Face Down:</span>
            <span :class="['status-badge', isFaceDown ? 'status-success' : 'status-neutral']">
              {{ isFaceDown ? '✓ Ja' : '— Nein' }}
            </span>
          </div>
          
          <div v-if="beta !== null" class="info-item">
            <span class="info-label">Beta (Neigung):</span>
            <code class="token-display">{{ beta.toFixed(2) }}°</code>
          </div>
        </div>

        <PrimaryButton
          v-if="!permissionGranted"
          @click="requestOrientationPermission"
          size="sm"
        >
          Berechtigung anfordern
        </PrimaryButton>
      </div>

      <!-- Actions -->
      <div class="settings-section">
        <h2>Aktionen</h2>
        <div class="action-buttons">
          <PrimaryButton
            v-if="isAuthenticated"
            variant="secondary"
            @click="handleLogout"
          >
            Von Spotify trennen
          </PrimaryButton>
          
          <PrimaryButton
            variant="ghost"
            @click="goHome"
          >
            Zurück zur Startseite
          </PrimaryButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useSpotifyAuth } from '@/composables/useSpotifyAuth';
import { useSpotifyPlayer } from '@/composables/useSpotifyPlayer';
import { useDeviceOrientation } from '@/composables/useDeviceOrientation';
import SpotifyStatusBadge from '@/components/Spotify/SpotifyStatusBadge.vue';
import PrimaryButton from '@/components/UI/PrimaryButton.vue';

const router = useRouter();

const { 
  accessToken, 
  userProfile, 
  isPremium, 
  isAuthenticated, 
  logout 
} = useSpotifyAuth();

const { 
  deviceId, 
  isReady,
  connect 
} = useSpotifyPlayer();

const {
  isFaceDown,
  beta,
  isSupported,
  permissionGranted,
  requestPermission
} = useDeviceOrientation();

const truncatedToken = computed(() => {
  if (!accessToken.value) return 'Nicht vorhanden';
  return `${accessToken.value.substring(0, 20)}...`;
});

async function connectPlayer() {
  await connect();
}

async function requestOrientationPermission() {
  await requestPermission();
}

function handleLogout() {
  logout();
  router.push('/');
}

function goHome() {
  router.push('/');
}
</script>

<style scoped lang="scss">
.settings-view {
  @apply min-h-screen;
  @apply px-4 py-20;
  background: linear-gradient(135deg, $background-base 0%, $background-dark 100%);
}

.content-container {
  @apply max-w-2xl mx-auto;
  @apply flex flex-col gap-6;
}

.settings-header {
  @apply text-center mb-4;
  
  h1 {
    @apply text-3xl font-bold text-white m-0;
  }
}

.settings-section {
  @apply bg-gray-900 bg-opacity-50 rounded-2xl p-6;
  @apply border border-gray-800;
  backdrop-filter: blur(10px);
  
  h2 {
    @apply text-xl font-semibold text-white m-0 mb-4;
    @apply pb-3 border-b border-gray-800;
  }
}

.info-grid {
  @apply flex flex-col gap-3;
}

.info-item {
  @apply flex items-center justify-between;
  @apply py-2;
}

.info-label {
  @apply text-gray-400 text-sm font-medium;
}

.info-value {
  @apply text-white text-sm;
}

.token-display {
  @apply bg-gray-800 text-green-400;
  @apply px-3 py-1 rounded;
  @apply text-xs font-mono;
  @apply max-w-xs overflow-hidden text-ellipsis;
}

.status-badge {
  @apply px-3 py-1 rounded-full text-xs font-medium;
}

.status-success {
  @apply bg-green-900 bg-opacity-30 text-green-400;
  border: 1px solid rgba($spotify-green, 0.3);
}

.status-error {
  @apply bg-red-900 bg-opacity-30 text-red-400;
  border: 1px solid rgba($error, 0.3);
}

.status-neutral {
  @apply bg-gray-800 text-gray-400;
}

.action-buttons {
  @apply flex flex-col gap-3;
}
</style>